const settings = {
  intersectionRatio: 0.5,
  scrollDirection: {
    horizontal: 'horizontal',
    horizontalReversed: 'horizontal-reversed',
    vertical: 'vertical',
    verticalReversed: 'vertical-reversed',
  },
  typeOfIntersecting: {
    maxVisibility: 'maximum-visibility-of-each-image-in-viewport',
    middleOfViewport: 'image-in-the-middle-of-viewport',
  },
};

if (!customElements.get('sticky-images-and-text')) {
  customElements.define(
    'sticky-images-and-text',
    class StickyImagesAndText extends HTMLElement {
      constructor() {
        super();

        this.performAnimation = null;
        this.lastOffset = 0;
        this.activeIndex = 0;
        this.lastActiveIndex = this.activeIndex;

        this.onScrollCallback = () => this.onScroll();
        this.onResizeCallback = () => this.onResize();
        this.calculateImagesOffsetCallback = () => this.calculateImagesOffset();
        this.onImagesSizeChangeCallback = (entries) => this.onImagesSizeChange(entries);
        this.onViewportResizeCallback = () => this.onViewportResize();
        this.lastViewportHeight = 0;
        this.viewportUpdateFrame = null;
      }

      connectedCallback() {
        this.scrollDirection = this.dataset.scrollDirection;
        this.horizontalScroll = this.scrollDirection === settings.scrollDirection.horizontal || this.scrollDirection === settings.scrollDirection.horizontalReversed;
        this.verticalScroll = this.scrollDirection === settings.scrollDirection.vertical || this.scrollDirection === settings.scrollDirection.verticalReversed;
        this.reverseDirection = this.scrollDirection === settings.scrollDirection.horizontalReversed || this.scrollDirection === settings.scrollDirection.verticalReversed;
        this.direction = this.reverseDirection ? 1 : -1;
        this.withGaps = this.hasAttribute('data-sticky-with-gaps');
        this.singleText = this.hasAttribute('data-sticky-single-text');
        this.image = this.querySelectorAll('.js-only [data-sticky-image]');
        this.text = this.querySelectorAll('.js-only [data-sticky-text]');
        this.textInner = this.querySelectorAll('.js-only [data-sticky-text-inner]');
        this.images = this.querySelector('[data-sticky-images]');
        this.dot = this.querySelectorAll('[data-sticky-dot]');
        this.aside = this.querySelector('[data-sticky-aside]');
        this.inner = this.querySelector('[data-sticky-inner]');
        this.content = this.querySelector('[data-sticky-content]');
        this.wrapper = this.querySelector('[data-sticky-wrapper]');
        this.textHighlight = this.querySelectorAll('text-highlight');

        if (!this.wrapper) return;

        // Wait for CSS/fonts so intersection is measured against real image heights
        const performInitialCalculation = () => {
          if (!this.isConnected) return;
          this.calculateHeights();
          this.getTypeOfIntersecting();
          this.observeImagesIntersecting();
          this.syncActiveFromViewport();
          this.performAnimation = requestAnimationFrame(this.calculateImagesOffsetCallback);
          // Recalculate after first pass to ensure accuracy
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.calculateHeights();
              this.getTypeOfIntersecting();
              this.syncActiveFromViewport();
            });
          });
        };

        const scheduleCalculation = () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(performInitialCalculation);
          });
        };

        // Wait for document ready state
        if (document.readyState === 'complete') {
          // Document already loaded - calculate immediately
          scheduleCalculation();
        } else {
          // Wait for load event (includes CSS, fonts, images)
          window.addEventListener('load', scheduleCalculation, {once: true});
        }

        document.addEventListener('theme:scroll', this.onScrollCallback);
        document.addEventListener('theme:resize:width', this.onResizeCallback);

        if (window.theme.isMobile() && window.visualViewport) {
          window.visualViewport.addEventListener('resize', this.onViewportResizeCallback);
        }

        this.observeImagesSize();

        this.syncDots();
      }

      getTypeOfIntersecting() {
        // Detect the maximum visibility of each image when intersecting with the viewport
        this.typeOfIntersecting = settings.typeOfIntersecting.maxVisibility;
        this.fractionOfViewport = this.image[0].offsetHeight / window.innerHeight;

        if (!this.verticalScroll || window.theme.isMobile()) return;

        if (this.fractionOfViewport < 0.6) {
          // Detect when each image is in the middle of viewport
          this.typeOfIntersecting = settings.typeOfIntersecting.middleOfViewport;
        }
      }

      observeImagesIntersecting() {
        if (this.observer) {
          this.observer.disconnect();
        }

        this.visibilityByImage = new Map();

        this.observer = new IntersectionObserver(
          (entries) => {
            if (this.typeOfIntersecting !== settings.typeOfIntersecting.maxVisibility) return;

            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.visibilityByImage.set(entry.target, entry.intersectionRatio);
              } else {
                this.visibilityByImage.delete(entry.target);
              }
            });

            this.syncActiveFromMaxVisibility();
          },
          {
            root: null,
            rootMargin: '0px',
            threshold: [0, 0.15, 0.3, 0.35, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7, 0.85, 1],
          }
        );

        this.image.forEach((image) => this.observer.observe(image));
      }

      syncActiveFromMaxVisibility() {
        if (!this.visibilityByImage || this.visibilityByImage.size === 0) return;

        let mostVisibleImage = null;
        let maxVisibility = -1;

        this.visibilityByImage.forEach((intersectionRatio, image) => {
          if (intersectionRatio < maxVisibility) return;
          if (intersectionRatio === maxVisibility && mostVisibleImage) {
            if (Number(image.dataset.index) >= Number(mostVisibleImage.dataset.index)) return;
          }
          maxVisibility = intersectionRatio;
          mostVisibleImage = image;
        });

        if (!mostVisibleImage) return;

        const windowHeight = this.windowHeight || window.innerHeight;
        let intersectAt = settings.intersectionRatio;
        if (windowHeight <= mostVisibleImage.offsetHeight) {
          intersectAt = 0;
        }

        if (maxVisibility <= intersectAt) return;

        this.setActiveIndex(Number(mostVisibleImage.dataset.index));
      }

      syncActiveFromViewport() {
        if (this.typeOfIntersecting === settings.typeOfIntersecting.middleOfViewport) {
          this.image.forEach((image) => this.checkImagePositionInViewport(image));
          return;
        }

        this.syncActiveFromMaxVisibility();
      }

      setActiveIndex(index) {
        this.activeIndex = index;

        if (this.lastActiveIndex !== this.activeIndex) {
          this.sync();
          this.lastActiveIndex = this.activeIndex;
        }
      }

      onScroll() {
        if (!this.performAnimation) {
          this.performAnimation = requestAnimationFrame(this.calculateImagesOffsetCallback);
        }

        if (this.typeOfIntersecting === settings.typeOfIntersecting.middleOfViewport) {
          this.image.forEach((image) => this.checkImagePositionInViewport(image));
        }
      }

      removeAnimationFrame() {
        if (this.performAnimation) {
          cancelAnimationFrame(this.performAnimation);
          this.performAnimation = null;
        }
      }

      checkImagePositionInViewport(element) {
        if (!element) return;

        const windowHeight = window.innerHeight;
        const scrollTop = window.scrollY;
        const scrollMiddle = scrollTop + windowHeight / 2;
        const scrollBottom = scrollTop + windowHeight;
        const elementOffsetTopPoint = Math.round(element.getBoundingClientRect().top + scrollTop);
        const elementHeight = element.offsetHeight;
        const elementOffsetBottomPoint = elementOffsetTopPoint + elementHeight;
        const isBottomOfElementPassed = elementOffsetBottomPoint < scrollTop;
        const isTopOfElementReached = elementOffsetTopPoint < scrollBottom;
        const isInView = isTopOfElementReached && !isBottomOfElementPassed;

        if (!isInView) return;

        if (scrollMiddle > elementOffsetTopPoint && elementOffsetBottomPoint > scrollMiddle) {
          this.setActiveIndex(Number(element.dataset.index));
        }
      }

      calculateImagesOffset() {
        const asideTop = this.aside.getBoundingClientRect().top - this.headerHeight;
        const stickyStartingPoint = asideTop - this.innerStickyTop;
        let percent = 0;
        let offset = 0;
        // Either offset images as soon as viewport top reaches the top point of the section,
        // or start the offset at the point where the inner container, that's holding all of the images, gets sticky
        const triggerPoint = !this.hasShortImages ? asideTop : stickyStartingPoint;

        if (triggerPoint < 0) {
          percent = (triggerPoint / this.bottomEndingPoint) * -1 * 100;
        } else if (triggerPoint >= 0) {
          percent = 0;
        } else {
          percent = 100;
        }

        offset = percent > 100 ? 100 : percent;
        offset *= this.image.length - 1;

        if (window.theme.isMobile() || this.horizontalScroll) {
          this.aside.style.setProperty('--translateX', `${Number(offset * this.direction).toFixed(2)}%`);
        }

        if (this.lastOffset !== offset) {
          this.performAnimation = requestAnimationFrame(this.calculateImagesOffsetCallback);
        } else if (this.performAnimation) {
          this.removeAnimationFrame();
        }

        this.lastOffset = offset;
      }

      observeImagesSize() {
        if (this.imagesSizeObserver) {
          this.imagesSizeObserver.disconnect();
        }

        if (typeof ResizeObserver === 'undefined' || !this.image[0]) return;

        this.imagesSizeObserver = new ResizeObserver(this.onImagesSizeChangeCallback);
        this.imagesSizeObserver.observe(this.image[0]);
      }

      onImagesSizeChange(entries) {
        const height = Math.round(entries[0]?.contentRect.height || 0);
        if (!height || height === this.imagesHeight) return;

        this.refreshLayoutMeasurements();
      }

      onViewportResize() {
        if (!window.theme.isMobile() || this.viewportUpdateFrame) return;

        this.viewportUpdateFrame = requestAnimationFrame(() => {
          this.viewportUpdateFrame = null;

          const viewportHeight = Math.round(window.visualViewport?.height || 0);
          if (!viewportHeight || viewportHeight === this.lastViewportHeight) return;

          this.lastViewportHeight = viewportHeight;
          this.refreshLayoutMeasurements();
        });
      }

      refreshLayoutMeasurements() {
        this.calculateHeights();
        this.getTypeOfIntersecting();
        this.syncActiveFromViewport();
        this.refreshImagesOffsetValues();
      }

      calculateHeights() {
        let {stickyHeaderHeight, windowHeight} = window.theme.readHeights();
        this.headerHeight = stickyHeaderHeight || 0;
        this.windowHeight = windowHeight || window.innerHeight;

        // Google in-app browser: 100vh is taller than the visible webview, so text
        // padding (--images-height) falls short of the rendered image.
        if (window.theme.isMobile()) {
          const viewportHeight = window.visualViewport?.height || this.windowHeight;
          this.windowHeight = viewportHeight;
          this.lastViewportHeight = Math.round(viewportHeight);
          this.style.setProperty('--full-screen', `${Math.round(viewportHeight - this.headerHeight)}px`);
        }

        // Content height variables
        if (this.text.length > 0) {
          const contentPaddingTop = this.content ? parseInt(window.getComputedStyle(this.content).getPropertyValue('padding-top')) : 0;
          const contentPaddingBottom = this.content ? parseInt(window.getComputedStyle(this.content).getPropertyValue('padding-bottom')) : 0;
          const textElementsHeights = [...this.text].map((element) => element.offsetHeight + contentPaddingTop + contentPaddingBottom);
          const textInnerElementsHeights = [...this.textInner].map((element) => element.offsetHeight);
          const maxTextHeight = Math.max(...textElementsHeights);
          const maxTextInnerHeight = Math.max(...textInnerElementsHeights);
          const highestText = window.theme.isMobile() ? maxTextInnerHeight : maxTextHeight;
          const textElementsHeightsSum = textElementsHeights.reduce((a, b) => a + b, 0);
          const averageTextHeight = Math.floor(textElementsHeightsSum / textElementsHeights.length) || this.text[0].offsetHeight;

          this.style.removeProperty('--average-text-height');
          this.style.setProperty('--average-text-height', `${averageTextHeight}px`);
          this.style.removeProperty('--highest-text');
          this.style.setProperty('--highest-text', `${highestText}px`);
        }

        // Images height variables
        const firstImage = this.image[0];
        this.imagesHeight = firstImage ? Math.round(firstImage.getBoundingClientRect().height) : this.images.offsetHeight;
        this.hasShortImages = false;

        if (this.image.length > 0) {
          const imageWrapperElement = this.image[0].querySelector('.image-wrapper');
          const videoWrapperElement = this.image[0].querySelector('.sti__video-wrapper');
          const wrapperElement = imageWrapperElement || videoWrapperElement;

          this.imgHeight = wrapperElement ? Math.round(wrapperElement.getBoundingClientRect().height) : 0;
          this.imgPadding = parseInt(window.getComputedStyle(this.image[0]).getPropertyValue('padding-top'));

          this.style.removeProperty('--images-height');
          this.style.setProperty('--images-height', `${this.imagesHeight}px`);
          this.style.removeProperty('--img-height');
          this.style.setProperty('--img-height', `${this.imgHeight + 2 * this.imgPadding}px`);
        }

        // Variables used in `calculateImagesOffset()` method
        this.asideHeight = this.aside.offsetHeight;
        this.asidePadding = parseInt(window.getComputedStyle(this.aside).getPropertyValue('padding-top'));
        this.breathingSpace = (this.windowHeight - this.imagesHeight) / 2 / 2;
        this.innerStickyTop = parseInt(window.getComputedStyle(this.inner).getPropertyValue('top')) - this.headerHeight - this.breathingSpace;
        this.gutters = this.withGaps ? 2 * this.asidePadding : 0;
        this.additionalSpace = window.theme.isMobile() ? (this.withGaps ? this.asidePadding + 10 : 10) : this.gutters;
        this.minHeightForEndingPoing = this.imagesHeight + this.additionalSpace;
        if (!window.theme.isMobile() && this.horizontalScroll) {
          this.minHeightForEndingPoing = this.windowHeight - this.headerHeight;

          // Update trigger point for images offset whenever each image height takes up less than 60% of the screen height
          this.hasShortImages = this.fractionOfViewport <= 0.6;
          if (this.hasShortImages) {
            this.minHeightForEndingPoing = this.imagesHeight - this.innerStickyTop + this.breathingSpace * 3;
          }
        }
        this.bottomEndingPoint = this.asideHeight - this.minHeightForEndingPoing;
      }

      refreshImagesOffsetValues() {
        // Double `requestAnimationFrame()` methods are used to make sure `calculateHeights()` gets executed first
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            document.dispatchEvent(new CustomEvent('theme:scroll'));
          });
        });
      }

      onResize() {
        this.calculateHeights();
        // Envoke `calculateHeights()` twice due to Theme editor issues
        requestAnimationFrame(() => this.calculateHeights());
        this.getTypeOfIntersecting();
        this.syncActiveFromViewport();
        this.refreshImagesOffsetValues();
      }

      sync() {
        const textsToSync = !this.singleText ? this.text : [];

        [...this.image, ...textsToSync, ...this.dot].forEach((element) => {
          const elementIndex = Number(element.getAttribute('data-index'));
          element.classList.toggle('is-active', elementIndex === Number(this.activeIndex));
        });

        this.textHighlight.forEach((highlight) => highlight.shouldAnimate());
      }

      syncDots() {
        this.dot.forEach((element) => {
          element.addEventListener('click', (event) => {
            event.preventDefault();
            this.scrollTo(this.image[Number(event.target.dataset.index)]);
          });
        });
      }

      scrollTo(target, timeout = 0) {
        if (!target) return;

        const activeIndex = Number(target.dataset.index);
        const targetOffsetTop = Math.round(target.getBoundingClientRect().top + window.scrollY);
        const targetHeight = target.offsetHeight;
        const containerTop = Math.round(this.wrapper.getBoundingClientRect().top + window.scrollY);
        const asideTop = this.aside.getBoundingClientRect().top - this.headerHeight;
        const imagesHeight = this.images.offsetHeight;
        const scrollOffset = containerTop + imagesHeight * activeIndex;
        const imageFitsInVh = Boolean((this.image[0].offsetHeight + this.gutters) / (window.innerHeight - this.headerHeight) >= 1);

        // Horizontal layouts with images (+ top/bottom gaps) >= viewport height, Desktop
        let distance = scrollOffset - this.headerHeight;

        // Horizontal + Vertical layouts, Mobile
        if (window.theme.isMobile()) {
          const triggerPoint = asideTop + window.scrollY;
          distance = triggerPoint + (this.bottomEndingPoint / (this.image.length - 1)) * activeIndex;
        }

        // Horizontal layouts with images (+ top/bottom gaps) < viewport height, Desktop
        if (this.horizontalScroll && window.theme.isDesktop() && !imageFitsInVh) {
          const stickyStartingPoint = asideTop - this.innerStickyTop;
          const triggerPoint = !this.hasShortImages ? asideTop + window.scrollY : stickyStartingPoint + window.scrollY;

          distance = triggerPoint + (this.bottomEndingPoint / (this.image.length - 1)) * activeIndex;
        }

        // Vertical layouts, Desktop
        if (this.verticalScroll && window.theme.isDesktop()) {
          // with images taking up more than 60% of the viewport height
          distance = targetOffsetTop - this.headerHeight;

          // with images taking up less than 60% of the viewport height
          if (this.typeOfIntersecting === settings.typeOfIntersecting.middleOfViewport) {
            distance = targetOffsetTop - window.innerHeight / 2 + targetHeight / 2 - this.headerHeight;
          }
        }

        const scrollOptions = {
          top: distance,
          left: 0,
          behavior: 'smooth',
        };

        timeout > 0 ? setTimeout(() => window.scrollTo(scrollOptions), timeout) : window.scrollTo(scrollOptions);
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }

        if (this.imagesSizeObserver) {
          this.imagesSizeObserver.disconnect();
        }

        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this.onViewportResizeCallback);
        }

        if (this.viewportUpdateFrame) {
          cancelAnimationFrame(this.viewportUpdateFrame);
          this.viewportUpdateFrame = null;
        }

        document.removeEventListener('theme:scroll', this.onScrollCallback);
        document.removeEventListener('theme:resize:width', this.onResizeCallback);
      }
    }
  );
}
